import { useState } from 'react';
import { components } from '@api/play-account/generated';
import { useLocation } from '@tanstack/react-router';

import { ClockControlIcon } from '@ui/icons/ClockControlIcon';

import { useAuth } from '@features/auth/hooks/useAuth';
import { ChartPoint, ClockControlType, EloHistoryPeriodType } from '@features/shared/api/typings';
import { TIME_CONTROL } from '@features/shared/constants';
import {
  usePlayerEloHistoryQuery,
  usePlayerEloStatsQuery,
  usePlayerGamesHistoryQuery,
} from '@features/user/api/hooks';
import { useGoToGame } from '@features/user/hooks/useGoToGame';
import { PlayerEloStat } from '@features/user/ui/PlayerEloStats/PlayerEloStat/PlayerEloStat';
import { PlayerShieldQuickPeekWidget } from '@features/user/ui/PlayerShieldQuickPeek/PlayerShieldQuickPeekWidget';

type Props = {
  clockControlType: ClockControlType;
  openShowFullGamesList: () => void;
};
export function PlayerEloStatWidget({ clockControlType, openShowFullGamesList }: Props) {
  const [periodType, setPeriodType] = useState<EloHistoryPeriodType>('Daily');
  const { user } = useAuth();

  const { data: stats } = usePlayerEloStatsQuery({
    userId: user?.userId,
  });

  const { data: eloHistory } = usePlayerEloHistoryQuery({
    periodType: periodType,
    periodsCount: 10,
    clockControlType,
    userId: user?.userId,
  });

  const { data: games } = usePlayerGamesHistoryQuery({
    userId: user?.userId,
    clockControlType,
    limit: 10,
  });

  const points = convertEloHistoryToChartPoints(eloHistory);

  const gameCount = stats?.[clockControlType.toLowerCase() as keyof typeof stats]?.gamesCount;

  const onGameClick = useGoToGame(useLocation().pathname);

  return (
    <PlayerEloStat
      title={TIME_CONTROL[clockControlType]}
      icon={<ClockControlIcon type={clockControlType} />}
      tab={periodType}
      onTabChange={setPeriodType}
      rank={eloHistory?.endElo}
      points={points}
      gameCount={gameCount}
      start={eloHistory?.startDateTimestampMs}
      end={eloHistory?.endDateTimestampMs}
      maxRank={eloHistory?.maxElo}
      minRank={eloHistory?.minElo}
      games={games}
      onGameClick={onGameClick}
      openShowFullGamesList={openShowFullGamesList}
      renderShieldQuickPeek={(userId) => <PlayerShieldQuickPeekWidget userId={userId} />}
    />
  );
}

function convertEloHistoryToChartPoints(
  eloHistory?: components['schemas']['UserEloHistoryDto'],
): ChartPoint[] | undefined {
  if (!eloHistory || !eloHistory.periods.length) return;

  const result: ChartPoint[] = [];

  eloHistory.periods.forEach((p) => {
    result.push({
      x: p.startDateTimestampMs,
      y: p.startElo,
      label: p.delta,
    });
  });

  result.push({
    x: eloHistory.periods[eloHistory.periods.length - 1]!.endDateTimestampMs,
    y: eloHistory.periods[eloHistory.periods.length - 1]!.endElo,
    label: null,
  });

  return result;
}
